import { Component, computed, DestroyRef, effect, inject, OnInit, untracked } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LessonDetailStateService } from '@lb/components/lesson-detail/lesson-detail-state.service';
import { LearnerLessonDetailComponent } from '@lb/components/lesson-detail/learner-lesson-detail/learner-lesson-detail.component';
import { InstructorLessonDetailComponent } from '@lb/components/lesson-detail/instructor-lesson-detail/instructor-lesson-detail.component';
import { LoaderComponent } from '@lb/components/loader/loader.component';
import { ResourceErrorComponent, ResourceErrorType } from '@lb/components/resource-error/resource-error.component';
import { MDIconComponent } from '@melodic-ui/components';
import { TimezoneAwareDatePipe } from '@lb/pipes';
import { EnrollmentStateService } from '@lb/state/enrollment-state.service';
import { ExternalLinksComponent } from '@lb/components/external-links/external-links.component';
import { USER_VIEW_TOKEN } from '../enrollment-detail.tokens';
import { LessonDetailFormStateService } from '@lb/components/lesson-detail/lesson-detail-form-state.service';
import { LessonSummaryWebhookService } from '@lb/components/lesson-detail/lesson-summary-webhook.service';
import { APP_CONFIG, IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { usesLessonDetailsV2 } from '@lb/functions';

@Component({
	selector: 'lb-enrollment-lesson-wrapper',
	standalone: true,
	imports: [LearnerLessonDetailComponent, InstructorLessonDetailComponent, LoaderComponent, ResourceErrorComponent, MDIconComponent, ExternalLinksComponent],
	templateUrl: './enrollment-lesson-wrapper.component.html',
	styleUrl: './enrollment-lesson-wrapper.component.scss',
	// LessonDetailStateService is intentionally NOT provided here so this wrapper shares the
	// instance from its ancestor (the enrollment-detail shell, or the route in the admin context).
	// Sharing the shell's instance lets timelineActiveLessonID drive the shell timeline's --current dot.
	providers: [TimezoneAwareDatePipe, LessonDetailFormStateService, LessonSummaryWebhookService]
})
export class EnrollmentLessonWrapperComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);

	readonly state = inject(LessonDetailStateService);
	private readonly enrollmentState = inject(EnrollmentStateService);

	protected readonly userView = inject(USER_VIEW_TOKEN, { optional: true }) ?? 'learner';
	private readonly _appSettings = inject<IAppSettings>(APP_CONFIG);

	programSummary = computed(() => this.enrollmentState.currentEnrollment()?.programSummary);
	enrollmentGUID = computed(() => this.enrollmentState.currentEnrollment()?.enrollmentGUID);

	isAbsent = this.state.isAbsent;

	// Read enrollment ID directly from the route snapshot — stable and available before the lesson loads.
	// Walk up the route tree because in some app configurations the shell is mounted at a static
	// path segment (e.g. 'lessons') rather than directly at ':id', so the immediate parent may
	// not carry the enrollment ID param.
	private readonly _enrollmentIDFromRoute = this._getEnrollmentIDFromRoute();

	private _getEnrollmentIDFromRoute(): number {
		let snapshot = this.route.snapshot.parent;
		while (snapshot) {
			const id = Number(snapshot.paramMap.get('id'));
			if (id) return id;
			snapshot = snapshot.parent;
		}
		return 0;
	}

	constructor() {
		// Instantiated eagerly. The form state hydrates itself from LessonDetailStateService through its
		// own effects (see LessonDetailFormStateService's constructor), and creating it here means those
		// effects exist as soon as the lesson and semester arrive rather than when the first detail
		// section mounts.
		inject(LessonDetailFormStateService);

		// v2 lesson detail only applies to eligible lessons (flag on + enrollment grading config
		// stamped). Deep links to ineligible lessons are sent back to the lesson list.
		effect(() => {
			const lesson = this.state.lesson();
			if (!lesson) return;
			if (!usesLessonDetailsV2(lesson, this._appSettings)) {
				untracked(() => this.router.navigate(['..'], { relativeTo: this.route, replaceUrl: true }));
			}
		});
	}

	lesson = this.state.lesson;
	// Gate the loader on ALL detail data (grading template, surveys, objectives, grade scale,
	// related lessons, enrollment detail) rather than just the primary lesson load. Otherwise the
	// detail mounts as soon as the lesson arrives, while the grading template is still loading, so
	// summaryOnly starts true (one column) and flips false (two columns) a beat later — a visible
	// layout jump. Mirrors the standalone lesson-detail route. resourceError() clears the loader
	// early so a load failure still surfaces instead of spinning forever.
	loading = computed(() => !this.state.detailContentReady() && !this.resourceError());
	hasError = this.state.hasError;

	enrollmentID = computed(() => this.lesson()?.enrollmentID ?? this._enrollmentIDFromRoute);

	// Route back to this enrollment's lesson list when the URL points to a valid enrollment;
	// otherwise fall back to the enrollments list so the user is never stranded.
	protected readonly lessonsLink = computed(() => (this._enrollmentIDFromRoute ? `/enrollments/${this._enrollmentIDFromRoute}/lessons` : '/enrollments'));

	// Single source of truth for resource-level failures, surfaced inline via <lb-resource-error>.
	// Precedence: a typed load failure (404/403/malformed) wins; otherwise a loaded lesson whose
	// enrollmentID disagrees with the URL is a bad combination of ids; otherwise any other sub-load
	// error falls back to a generic message.
	protected readonly resourceError = computed<ResourceErrorType | null>(() => {
		const typed = this.state.lessonErrorType();
		if (typed) return typed;

		const lesson = this.lesson();
		const routeEnrollmentID = this._enrollmentIDFromRoute;
		if (lesson && routeEnrollmentID && lesson.enrollmentID !== routeEnrollmentID) {
			return 'invalid';
		}

		return this.hasError() ? 'error' : null;
	});

	lessonNumber = computed(() => {
		const id = this.state.lessonID();
		const sorted = [...this.state.relatedLessons()]
			.filter((l) => l.lessonTypeID === 2 && l.attendanceStatusID !== 2 && l.lessonStatusID !== 3 && l.lessonStatusID !== 4)
			.sort((a, b) => new Date(a.lessonDate).getTime() - new Date(b.lessonDate).getTime());
		const current = this.state.lesson();
		if (!current || current.lessonTypeID !== 2 || current.attendanceStatusID === 2 || current.lessonStatusID === 3 || current.lessonStatusID === 4)
			return null;
		const idx = sorted.findIndex((l) => l.lessonID === id);
		return idx >= 0 ? idx + 1 : null;
	});

	prevLesson = computed(() => {
		const id = this.state.lessonID();
		const sorted = [...this.state.relatedLessons()].sort((a, b) => new Date(a.lessonDate).getTime() - new Date(b.lessonDate).getTime());
		const idx = sorted.findIndex((l) => l.lessonID === id);
		return idx > 0 ? sorted[idx - 1] : null;
	});

	nextLesson = computed(() => {
		const id = this.state.lessonID();
		const sorted = [...this.state.relatedLessons()].sort((a, b) => new Date(a.lessonDate).getTime() - new Date(b.lessonDate).getTime());
		const idx = sorted.findIndex((l) => l.lessonID === id);
		return idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null;
	});

	ngOnInit(): void {
		if (!(this._appSettings.features?.enableLessonDetails ?? false)) {
			this.router.navigate(['..'], { relativeTo: this.route, replaceUrl: true });
			return;
		}

		this.state.navigationSource.set({ type: 'enrollment', id: this.enrollmentID() });

		this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
			if (!params.has('lessonId')) return;

			// Call loadLesson even when the id doesn't parse (e.g. /lessons/abc -> NaN) so the
			// state service flags it as an invalid link instead of leaving the view blank.
			const lessonID = Number(params.get('lessonId'));
			this.state.loadLesson(lessonID);

			if (lessonID) {
				this.state.activeLessonID.set(lessonID);
				this.state.timelineActiveLessonID.set(lessonID);
			}
		});
	}

	goBack(): void {
		const lessonID = this.state.lessonID();
		this.router.navigate(['/enrollments', this.enrollmentID(), 'lessons'], {
			queryParams: lessonID ? { lesson: lessonID } : {}
		});
	}

	navigateToLesson(lessonID: number): void {
		this.router.navigate(['/enrollments', this.enrollmentID(), 'lessons', lessonID]);
	}
}
